import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { toast } from 'react-toastify';
import Api from '../../util/api';
import useConfig from '../../util/useConfig';
import t, { partial } from '../../util/translation/translation';
import MatchCenterHelpers from './helpers';
import useModal from '../../shared/hooks/useModal';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import Loading from '../../shared/components/custom/Loading';

import ViewIcon from 'mdi-react/EyeOffIcon';
import LinkIcon from 'mdi-react/LinkVariantIcon';
import CalendarIcon from 'mdi-react/CalendarIcon';

const UpcomingMatches = () => {
  const config = useConfig();
  const mc = partial('matchCenter');
  const df = partial('DateFormat');
  const lm = partial('ListMatches');
  const [handleModal, visible, deleteId] = useModal();
  const { addDash, getCompany, getSponsor } = MatchCenterHelpers;
  const [matches, setMatches] = useState([]);
  const [paginationDetails, setPaginationDetails] = useState({
    first: 1,
    last: 1,
    previous: null,
    next: null,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(3);
  const {
    data: upcomingMatches,
    isLoading,
    refetch: refecthUpcoming,
  } = useQuery({
    queryKey: ['upcoming-matches-banner', currentPage, pageSize],
    queryFn: () =>
      Api.matchCenter.getUpcomingMatches(
        currentPage,
        pageSize,
        config.teams.map((team) => team.id),
        false
      ),
  });
  const [loading, setLoading] = useState(false);

  const { data: companies } = useQuery({
    queryKey: ['club-companies'],
    queryFn: Api.companies.getCompaniesForClub,
  });

  useEffect(() => {
    if (!upcomingMatches) return;
    const results = upcomingMatches.data;
    setMatches(results.matches);
    setPaginationDetails({
      first: results.first,
      last: results.last,
      previous: results.previous,
      next: results.next,
      total: results.total,
    });
  }, [upcomingMatches, isLoading]);

  const renderCard = (renderMatches) => {
    return renderMatches.map((match) => (
      <Link
        key={match.id}
        to={`/matches/upcoming/edit/${match.id}`}
        className="flex flex-column hover-link"
      >
        {match?.homeTeam?.name} - {match?.awayTeam?.name}
        <div className="flex flex-row justify-content-start mb-2">
          <div className="flex flex-column space-between mr-3">
            <div>
              <span style={{ fontSize: 12, color: '#818181' }}>
                <CalendarIcon
                  size={16}
                  style={{ marginTop: -2, marginRight: 4 }}
                  color="#818181"
                />
                {moment
                  .utc(match.matchStart)
                  .local(true)
                  .format(df('getBaseDateTime'))}
              </span>
            </div>
            {!match?.settings?.visibleInBanner ? (
              <div style={{ fontSize: 12, color: '#818181' }}>
                <ViewIcon
                  size={12}
                  style={{ marginTop: -2, marginRight: 4 }}
                  color="#eb4747"
                />{' '}
                {lm('missingInBanner')}
              </div>
            ) : null}
            {!match?.settings?.settings?.ticket?.link ? (
              <div style={{ fontSize: 12, color: '#818181' }}>
                <LinkIcon
                  size={12}
                  style={{ marginTop: -2, marginRight: 4 }}
                  color="#eb4747"
                />{' '}
                {lm('missingTicketLink')}
              </div>
            ) : null}
          </div>

          <div className="flex-1">
            <div>
              <span style={{ fontSize: 8, color: '#818181', display: 'block' }}>
                {t('matchCenter.form.matchSponsorName')}:
              </span>
              {addDash(getSponsor(match).name.label)}
            </div>
            <div>
              <span style={{ fontSize: 8, color: '#818181', display: 'block' }}>
                {t('matchCenter.form.playerRatingSponsor')}:
              </span>{' '}
              <span>{addDash(getCompany(match, companies).label)}</span>
            </div>
          </div>
        </div>
      </Link>
    ));
  };

  const tableLayout = (renderMatches, _loading) => {
    return <>{renderMatches.length > 0 && renderCard(renderMatches)}</>;
  };

  const deleteMatch = async () => {
    setLoading(true);
    try {
      await Api.matchCenter.deleteMatch(deleteId);
      toast.success(mc('matchDeleted'));
    } catch (err) {
      toast.error(err);
    }
    handleModal();
    setLoading(false);
    refecthUpcoming();
  };

  return (
    <Container>
      <Loading loading={loading} />
      <DeleteModal
        visible={visible}
        handleModal={handleModal}
        type={lm('theMatch')}
        modalAction={deleteMatch}
      />
      {tableLayout(
        [...matches],
        paginationDetails,
        lm('comingMatches'),
        isLoading,
        false
      )}
    </Container>
  );
};
export default UpcomingMatches;
